import { ToolboxComponent } from '../toolbox/ToolboxComponent'

import './scss/contentmenu.scss'

// Content menu icon height, used to calculate top offset
const DEFAULT_ICON_HEIGHT = 28

// Default position of pencil/menu trigger
const DEFAULT_TOP = 50
const DEFAULT_LEFT = 51

class ContentMenuComponent extends ToolboxComponent {

    constructor(...args) {
        super(...args)

        this._top = DEFAULT_TOP
        this._left = DEFAULT_LEFT
    }

    /**
     * Set initial state
     */
    getInitialState() {
        return {
            ...super.getInitialState(),
            open: false,
            style: this.context.configurator.getWriterConfigValue('contentMenuStyle', 'normal') // Also supports "dense"
        }
    }

    /**
     * Rerender content menu either if ToolboxComponents wants it or when local open state changes
     *
     * @param {object} newProps
     * @param {object} newState
     * @return {boolean}
     */
    shouldRerender(newProps, newState) {
        if (super.shouldRerender(newProps, newState) || newState.open !== this.state.open) {
            return true
        }

        return false
    }

    /**
     * Overridden from ToolboxCompoent. Called by ToolboxComponent prior to updating it's
     * state after a selection or context menu change.
     */
    onStateChange() {
        // Hide menu when editor state changes
        this.extendState({
            open: false
        })
    }

    render($$) {
        if (!this.state.hasActiveTools) {
            return $$('span')
        }

        const el = $$('div')
            .addClass('dw-toolbox-wrapper')
            .addClass('dw-toolbox-menu')
            .addClass('dw-toolbox-menu-with-trigger')

        const toggleEl = $$('div')
            .addClass('dw-toolbox-menu-trigger left')
            .on('click', this._onToggle)

        if (!this.state.open) {
            el.addClass('dw-hidden')

            return el.append(
                toggleEl.append(
                    $$(this.getComponent('button'), { icon: 'content-menu-open' })
                )
            )
        }

        el.append(
            toggleEl.append(
                $$(this.getComponent('button'), {icon: 'content-menu-close'})
            )
        )

        const availableToolsEl = $$('div')
            .addClass('dw-toolbox-menu')

        this.state.activeToolGroups.forEach((toolGroup) => {
            availableToolsEl.append(
                $$(toolGroup.Class, Object.assign({}, toolGroup, {
                    showIcons: true
                }))
                    .addClass(`sm-target-content-top-menu-${this.state.style}`)
            )
        })

        return el.append(availableToolsEl)
    }

    /*
     Update and re-position
     */
    didUpdate() {
        if (this.state.hints) {
            this._top = this._getTopOffsetFromContentStyle(this.state.hints)
            this.el.css('top', this._top)
        }
    }

    /**
     * Called by super class to allow contentmenu to override decision on
     * which toolgroups should be enabled in certain circumstances.
     *
     * @param {string} toolGroupName
     * @param {object} sel
     * @return {boolean}
     */
    shouldToolGroupBeEnabled(toolGroupName, sel) {

        if (!sel || !sel.surfaceId || sel.isNull()) {
            return false
        }

        if (sel.isContainerSelection() && toolGroupName === 'content-top-menu') {
            return false
        }

        if (sel.isNodeSelection() && toolGroupName === "content-menu") {
            return false
        }

        return true
    }

    overrideCommandStates(toolGroupName, commandStates, sel) {
        if (sel.isContainerSelection() && toolGroupName === "content-menu") {
            commandStates['switch-text-type'].disabled = false
        }
    }

    _onToggle(e) {
        e.stopPropagation()
        e.preventDefault()

        this.extendState({
            open: !this.state.open
        })
    }

    /**
     * @param hints
     * @returns {number} Integer
     */
    _getTopOffsetFromContentStyle(hints) {
        try {
            const sel = this.context.editorSession.getSelection()
            if (sel.isNull()) {
                return 0
            }

            const nodeId = this._getNodeIdFromSelection(sel)

            if (nodeId) {
                const surface = this.context.editorSession.getFocusedSurface()
                const domNode = surface.getDomNodeForId(nodeId)
                if (!domNode) {
                    return 0 // Selected node id not in focused surface, content menu won't show
                }

                const style = domNode.currentStyle || window.getComputedStyle(domNode, false)
                const lineHeight = parseInt(style.getPropertyValue('line-height'), 10)
                const rect = domNode.getBoundingClientRect()

                return Math.floor(rect.top - hints.contentRect.top + ((lineHeight - DEFAULT_ICON_HEIGHT) / 2))
            }
            else {
                console.warn('Failed to get content menu offset: no node ID found in selection', sel)
                return 0
            }
        }
        catch(err) {
            console.warn('Failed to get content menu offset:', err)
            return 0
        }
    }

    _getNodeIdFromSelection(sel) {
        if (!sel) {
            return
        }

        if (sel.type === 'node' && sel.getNodeId) {
            return sel.getNodeId()
        }
        else if (sel.type === 'property' && sel.getNodeId) {
            return sel.getNodeId()
        }
        else if (sel.type === 'container' && sel.getNodeIds) {
            const nodeIds = sel.getNodeIds()
            return sel.reverse ? nodeIds.shift() : nodeIds.pop()
        }
    }

    getActiveToolGroupNames() {
        return [
            'content-top-menu',
            'content-menu'
        ]
    }
}

export {ContentMenuComponent}
